import { ControlType } from "../enums/ControlType";
import { Condition, ICondition, IFilter, IFontSize, IMinMax, IMultiSelect, IPlaceholder, IReadonly, IRows, ISelectOption, IShowHorizontal, ISourceDatasetDinamico, ITooltip } from "../../services/form-builder/form.interface";
import { BaseControl, Options } from "./form-base-controls";
import { BehaviorSubject } from "rxjs";
import { MenuItem } from "primeng/api";


export const VisibleControls: MenuItem[] = [
  { label: 'Text', id: ControlType.Text },
  { label: 'Text Area', id: ControlType.TextArea },
  { label: 'Input Mask', id: ControlType.InputMask },
  { label: 'Number', id: ControlType.Number },
  { label: 'Label', id: ControlType.Label },
  { label: 'Dropdown', id: ControlType.Dropdown },
  { label: 'Calendar', id: ControlType.Calendar },
  { label: 'Checkbox', id: ControlType.Checkbox },
  { label: 'File Upload', id: ControlType.FileUpload },
  { label: 'Sub Formulario', id: ControlType.SubFormulario }
];

export class DropdownControl extends Options<ControlType.Dropdown> implements IPlaceholder, IFilter, ISourceDatasetDinamico, ITooltip, IReadonly {
  private _placeholder?: string;
  private _filter?: boolean

  private _columnName?: string;
  private _columnValue?: string;
  private _tablaConsulta?: number;
  private _tooltip?: string;
  private _readonly?: boolean;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Dropdown;
    this.placeholder = data.placeholder;
    this.filter = data.filter;
    this.columnName = data.columnName;
    this.columnValue = data.columnValue;
    this.tablaConsulta = data.tablaConsulta; 
    this.readonly = data.readonly;   
    if (this.tablaConsulta) {
      this.loadExternalDatasource = true;
    }
  }

  get placeholder(): string {
    return this._placeholder ?? '';
  }

  set placeholder(value: string) {
    this._placeholder = (value === '') ? undefined : value;
  }

  get filter(): boolean {
    return this._filter ?? false;
  }

  set filter(value: boolean) {
    this._filter = (value === false) ? undefined : value;
  }

  get columnName(): string {
    return this._columnName ?? '';
  }

  set columnName(value: string) {
    this._columnName = (value === '') ? undefined : value;
  }

  get columnValue(): string {
    return this._columnValue ?? '';
  }

  set columnValue(value: string) {
    this._columnValue = (value === '') ? undefined : value;
  }

  get tablaConsulta(): number | undefined {
    return this._tablaConsulta ?? undefined;
  }

  set tablaConsulta(value: number | undefined) {
    this._tablaConsulta = value;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  get readonly(): boolean {
    return this._readonly ?? false;
  }

  set readonly(value: boolean) {
    this._readonly = (value === false) ? undefined : value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      placeholder: this._placeholder,
      filter: this.filter,
      columnName: this._columnName,
      columnValue: this._columnValue,
      tablaConsulta: this._tablaConsulta,
      tooltip: this._tooltip,
      readonly: this._readonly
    }
  }
}

export class CheckboxControl extends Options<ControlType.Checkbox> implements IShowHorizontal, IMultiSelect, ITooltip {
  private _showHorizontal?: boolean;
  private _multiSelect?: boolean;
  private _tooltip?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Checkbox;
    this.showHorizontal = data.showHorizontal;
    this.multiSelect = data.multiSelect;
    this.tooltip = data.tooltip;
  }

  get showHorizontal(): boolean {
    return this._showHorizontal ?? false;
  }

  set showHorizontal(value: boolean) {
    this._showHorizontal = (value === false) ? undefined : value;
  }

  get multiSelect(): boolean {
    return this._multiSelect ?? true;
  }

  set multiSelect(value: boolean) {
    this._multiSelect = (value === true) ? undefined : value;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      showHorizontal: this._showHorizontal,
      multiSelect: this._multiSelect,
      tooltip: this._tooltip,
    }
  }
}

export class InputTextControl extends BaseControl<ControlType.Text> implements IPlaceholder, ITooltip, IReadonly {
  private _placeholder?: string;
  private _tooltip?: string;
  private _readonly?: boolean;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Text;
    this.placeholder = data.placeholder;
    this.tooltip = data.tooltip;
    this.readonly = data.readonly;
  }

  get placeholder(): string {
    return this._placeholder ?? '';
  }

  set placeholder(value: string) {
    this._placeholder = (value === '') ? undefined : value
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  get readonly(): boolean {
    return this._readonly ?? false;
  }

  set readonly(value: boolean) {
    this._readonly = (value === false) ? undefined : value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      tooltip: this._tooltip,
      placeholder: this._placeholder,
      readonly: this._readonly
    }
  }
}

export class InputNumberControl extends BaseControl<ControlType.Number> implements IMinMax, ITooltip, IReadonly {
  private _min?: number;
  private _max?: number;
  private _tooltip?: string;
  private _readonly?: boolean;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Number;
    this.min = data.min;
    this.max = data.max;
    this.tooltip = data.tooltip;
    this.readonly = data.readonly;
  }

  get min(): number | undefined {
    return this._min ?? undefined;
  }

  set min(value: number) {
    this._min = value ?? undefined;
  }

  get max(): number | undefined {
    return this._max ?? undefined;
  }

  set max(value: number) {
    this._max = value ?? undefined;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  get readonly(): boolean {
    return this._readonly ?? false;
  }

  set readonly(value: boolean) {
    this._readonly = (value === false) ? undefined : value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      min: this._min,
      max: this._max,
      tooltip: this._tooltip,
      readonly: this._readonly
    }
  }
}

export class CalendarControl extends BaseControl<ControlType.Calendar> implements ITooltip {
  private _dateFormat?: string;
  private _tooltip?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Calendar;
    this.dateFormat = data.dateFormat;
    this.tooltip = data.tooltip;
  }

  get dateFormat(): string {
    return this._dateFormat ?? 'dd/mm/yy';
  }

  set dateFormat(value: string) {
    this._dateFormat = (value === 'dd/mm/yy') ? undefined : value;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  override get conditionList(): ICondition[] {
    return [
      { label: 'Empty', value: Condition.Empty },
      { label: 'Not Empty', value: Condition.NotEmpty },
      { label: 'Equals', value: Condition.Equals },
      { label: 'Greater Than or Equals Years', value: Condition.GreaterThanOrEqualsYears },
      { label: 'Less Than or Equals Years', value: Condition.LessThanOrEqualsYears },
    ];
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      dateFormat: this._dateFormat,
      tooltip: this._tooltip,
    }
  }
}

export class FileUploadControl extends BaseControl<ControlType.FileUpload> implements ITooltip {
  private _chooseLabel?: string;
  private _accept?: string[];
  private _downloadFileMode?: boolean = false;
  private _multipleFiles?: boolean = false;
  private _tooltip?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.FileUpload;
    this.chooseLabel = data.chooseLabel;
    this.accept = data.accept;
    this.multipleFiles = data.multipleFiles;
    this.tooltip = data.tooltip;
  }

  get chooseLabel(): string {
    return this._chooseLabel ?? 'Browse';
  }

  set chooseLabel(value: string) {
    this._chooseLabel = (value === 'Browse') ? undefined : value;
  }

  get accept(): string[] {
    return this._accept ?? ['application/pdf'];
  }

  set accept(value: string[]) {
    this._accept = (value?.length === 1 && value[0] === 'application/pdf') || (value?.length === 0) ? undefined : value;
  }

  get downloadFileMode(): boolean {
    return this._downloadFileMode ?? false;
  }

  set downloadFileMode(value: boolean) {
    this._downloadFileMode = (value === false) ? undefined : value;
  }

  get multipleFiles(): boolean {
    return this._multipleFiles ?? false;
  }

  set multipleFiles(value: boolean) {
    this._multipleFiles = (value === false) ? undefined : value;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      chooseLabel: this._chooseLabel,
      accept: this._accept,
      multipleFiles: this._multipleFiles,
      tooltip: this._tooltip,
    }
  }
}

export class InputMaskControl extends BaseControl<ControlType.InputMask> implements IPlaceholder, ITooltip {
  private _placeholder?: string;
  private _mask?: string;
  private _tooltip?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.InputMask;
    this._mask = data.mask;
    this.placeholder = data.placeholder;
    this.tooltip = data.tooltip;
  }

  get placeholder(): string {
    return this._placeholder ?? '';
  }

  set placeholder(value: string) {
    this._placeholder = (value === '') ? undefined : value
  }

  get mask(): string {
    return this._mask ?? '';
  }

  set mask(value: string) {
    this._mask = (value === '') ? undefined : value;
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      placeholder: this._placeholder,
      mask: this._mask,
      tooltip: this._tooltip,
    }
  }
}

export class LabelControl extends BaseControl<ControlType.Label> implements IFontSize {
  private _fontSize?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.Label;
    this.fontSize = data.fontSize;
  }

  get fontSize(): string {
    return this._fontSize ?? 'text-xl';
  }

  set fontSize(value: string) {
    this._fontSize = (value === 'text-xl') ? undefined : value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      fontSize: this._fontSize
    }
  }
}

export class InputTextareaControl extends BaseControl<ControlType.TextArea> implements IRows, ITooltip {

  private _rows?: number;
  private _tooltip?: string;

  constructor(data: any) {
    super(data);
    this._type = ControlType.TextArea;
    this.rows = data.rows;
    this.tooltip = data.tooltip;
  }

  get rows(): number {
    return this._rows ?? 5;
  }

  set rows(value: number) {
    this._rows = (value === 5) ? undefined : value
  }

  get tooltip(): string {
    return this._tooltip ?? '';
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      rows: this._rows,
      tooltip: this._tooltip,
    }
  }
}

export class SubFormularioControl extends BaseControl<ControlType.SubFormulario> {

  private _formId$ = new BehaviorSubject<number | undefined>(undefined);
  private _version: number | undefined;
  private _removeAddBtn?: boolean = false;
  private _removeDeleteBtn?: boolean = false;

  constructor(data: any) {
    super(data);
    this._type = ControlType.SubFormulario;
    this.formId = data.formId;
    this.version = data.version;
    this.removeAddBtn = data.removeAddBtn;
    this.removeDeleteBtn = data.removeDeleteBtn;
  }

  // Observable to allow subscriptions
  get formId$() {
    return this._formId$.asObservable();
  }

  // Getter for the current value of formId
  get formId(): number | undefined {
    return this._formId$.value;
  }

  // Setter that emits changes to the BehaviorSubject
  set formId(value: number | undefined) {
    this._formId$.next(value);
  }

  get version(): number | undefined {
    return this._version;
  }

  set version(value: number | undefined) {
    this._version = value;
  }

  get removeAddBtn(): boolean {
    return this._removeAddBtn ?? false;
  }

  set removeAddBtn(value: boolean) {
    this._removeAddBtn = (value === false) ? undefined : value;
  }

  get removeDeleteBtn(): boolean {
    return this._removeDeleteBtn ?? false;
  }

  set removeDeleteBtn(value: boolean) {
    this._removeDeleteBtn = (value === false) ? undefined : value;
  }

  override toJSON(): any {
    return {
      ...super.toJSON(),
      formId: this._formId$.value,
      version: this._version,
      removeAddBtn: this._removeAddBtn,
      removeDeleteBtn: this._removeDeleteBtn
    };
  }
}


